import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import ConstructionGantt from '../components/ConstructionGantt';
import { pickContractorTeamId } from '../pickContractorTeam';
import { getAuth, isCompanyAdmin, setAuth, getToken } from '../authStorage';

import 'frappe-gantt/dist/frappe-gantt.css';

const LAST_PROJECT_KEY = 'ganttLastProjectId';
const LAST_TEAM_KEY = 'ganttLastTeamId';

function readStoredProjectId() {
  try {
    return sessionStorage.getItem(LAST_PROJECT_KEY) || '';
  } catch {
    return '';
  }
}

const GanttPage = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const sp = new URLSearchParams(search);
  const projectFromUrl = sp.get('project') || '';
  const projectId = projectFromUrl || readStoredProjectId();
  const teamFromUrl = sp.get('team') || '';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [teamsReady, setTeamsReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [ganttTeam, setGanttTeam] = useState(teamFromUrl);

  useEffect(() => {
    setGanttTeam(teamFromUrl);
  }, [teamFromUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      /* Luôn đồng bộ profile — tránh localStorage cũ khiến admin bị coi như nhà thầu (không gửi team_id → lỗi API). */
      try {
        const me = await api.get('/auth/me');
        if (!cancelled && me.data?.user) {
          const tok = getToken();
          if (tok) setAuth({ token: tok, user: me.data.user, teams: me.data.teams || [] });
        }
      } catch {
        /* ignore */
      }
      if (cancelled) return;
      if (!projectId) {
        setTeams([]);
        setTeamsReady(true);
        return;
      }
      setTeamsReady(false);
      try {
        const res = await api.get(`/projects/${projectId}/teams`, { params: { _ts: Date.now() } });
        if (!cancelled) setTeams(res.data || []);
      } catch {
        if (!cancelled) setTeams([]);
      } finally {
        if (!cancelled) setTeamsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (isCompanyAdmin() || teams.length === 0 || !projectId) return undefined;
    const auth = getAuth();
    const autoId = pickContractorTeamId(teams, auth?.teams, projectId, auth?.user?.id);
    if (!autoId) return undefined;
    if (String(teamFromUrl) === autoId) return undefined;
    const p = new URLSearchParams();
    p.set('project', projectId);
    p.set('team', autoId);
    navigate(`/gantt?${p.toString()}`, { replace: true });
    return undefined;
  }, [teams, projectId, navigate, teamFromUrl]);

  /* Quản trị: mặc định xem cả dự án (không ép ?team=). Tự chèn team khi chỉ có 1 đội dễ lọc nhầm và trả về biểu đồ trống. */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) {
        setTasks([]);
        setLoadError('');
        setLoading(false);
        return;
      }
      if (!teamsReady) {
        setLoading(true);
        return;
      }

      const auth = getAuth();
      let teamParam = '';
      if (isCompanyAdmin()) {
        teamParam = teamFromUrl || ganttTeam || '';
      } else {
        teamParam =
          teamFromUrl ||
          ganttTeam ||
          pickContractorTeamId(teams, auth?.teams, projectId, auth?.user?.id) ||
          '';
        if (!teamParam) {
          if (!cancelled) {
            setTasks([]);
            setLoadError(
              'Chưa chọn được đội trên dự án này — hãy vào Tiến độ, chọn đúng dự án và đội, hoặc mở Gantt từ trang chi tiết dự án.'
            );
            setLoading(false);
          }
          return;
        }
      }

      if (!cancelled) {
        setLoading(true);
        setLoadError('');
      }
      try {
        const params = { _ts: Date.now() };
        if (teamParam) params.team_id = teamParam;
        const res = await api.get(`/projects/${projectId}/gantt-tasks`, { params });
        const data = res.data;
        if (cancelled) return;
        if (Array.isArray(data)) {
          setTasks(data);
          try {
            sessionStorage.setItem(LAST_PROJECT_KEY, String(projectId));
            if (teamParam) sessionStorage.setItem(LAST_TEAM_KEY, String(teamParam));
            else sessionStorage.removeItem(LAST_TEAM_KEY);
          } catch {
            /* ignore */
          }
        } else {
          setTasks([]);
        }
      } catch (e) {
        if (!cancelled) {
          setTasks([]);
          setLoadError(e.response?.data?.error || e.message || 'Không tải được biểu đồ');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, ganttTeam, teamFromUrl, teams, teamsReady]);

  if (!projectId) {
    return (
      <div style={{ padding: 16, maxWidth: 560 }}>
        <p style={{ marginBottom: 12 }}>
          Chưa có <strong>dự án</strong> trong đường dẫn. Mở Gantt từ{' '}
          <Link to="/progress">Tiến độ</Link> hoặc <Link to="/projects">Danh sách dự án</Link> (link
          &quot;Gantt&quot; trên từng dự án).
        </p>
      </div>
    );
  }

  if (!teamsReady) {
    return (
      <div style={{ padding: 16 }}>
        <p aria-busy="true">Đang tải…</p>
      </div>
    );
  }

  const adminTeamValue = teamFromUrl || ganttTeam || '';
  const progressHref = `/progress?project=${encodeURIComponent(projectId)}${
    adminTeamValue ? `&team=${encodeURIComponent(adminTeamValue)}` : ''
  }`;

  return (
    <div className="gantt-page" style={{ width: '100%', minWidth: 0, maxWidth: '100%', minHeight: 320, overflowX: 'hidden' }}>
      {isCompanyAdmin() && teams.length > 0 ? (
        <div className="admin-toolbar">
          <label className="admin-toolbar__field">
            <span>Đội</span>
            <select
              value={adminTeamValue}
              aria-label="Lọc biểu đồ Gantt theo đội"
              onChange={(e) => {
                const v = e.target.value;
                try {
                  if (v) sessionStorage.setItem(LAST_TEAM_KEY, String(v));
                  else sessionStorage.removeItem(LAST_TEAM_KEY);
                } catch {
                  /* ignore */
                }
                const p = new URLSearchParams();
                p.set('project', String(projectId));
                if (v) p.set('team', v);
                navigate(`/gantt?${p.toString()}`, { replace: true });
                setGanttTeam(v);
              }}
            >
              <option value="">— Tất cả đội (cả dự án) —</option>
              {teams.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <Link to={progressHref} className="admin-toolbar__link">
            Tiến độ (cùng dự án/đội)
          </Link>
        </div>
      ) : null}

      {loading ? (
        <div style={{ minHeight: 280 }} aria-busy="true" />
      ) : (
        <>
          {loadError ? (
            <p className="page-alert page-alert--error">{loadError}</p>
          ) : null}
          <ConstructionGantt tasks={tasks} viewMode="Day" />
          {!loadError && tasks.length === 0 ? (
            <p className="page-muted-box">
              Dự án <strong>#{projectId}</strong>: API không trả về công việc nào (sau khi loại dòng thiếu ngày).
              Kiểm tra đã <strong>import Excel</strong> / có công việc trên đúng dự án; nhà thầu có thể đang làm{' '}
              <strong>dự án khác</strong> (đổi <code>?project=</code> hoặc vào{' '}
              <Link to="/progress">Tiến độ</Link> chọn dự án). Backend cần chạy để tải dữ liệu (
              <code>/projects/{projectId}/gantt-tasks</code>).
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default GanttPage;
